import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { User } from 'src/app/models/User';
import { ConfirmationService } from 'src/app/shared/components/confirmation-modal/confirmation.service';

import { environment } from './../../../environments/environment';
import { Affiliate, AffiliatesPage } from './../../models/Affiliate';
import { PartnerInfo } from './../../models/PartnerInfo';
import { ToastService } from './../../shared/components/toast/toast.service';
import { AuthService } from './../../shared/services/auth.service';
import { ADMINISTRATOR, CUSTOMER_SUCCESS, UserService } from './../../shared/services/user.service';
import { AGENCY_CLIENT } from '../../shared/services/user.service';
import { AgencyFilter } from 'src/app/models/AgencyFilter';

type SortKey = 'name' | 'balance';
type SortDir = 'asc' | 'desc';
type TypeFilter = 'all' | 'wallet' | 'credits';

@Component({
  selector: 'app-partner-dashboard',
  templateUrl: './partner-dashboard.component.html',
  styleUrls: ['./partner-dashboard.component.scss']
})
export class PartnerDashboardComponent implements OnInit {
  page = 1;
  filter = '';
  perPage = 10;
  type: TypeFilter = 'all';
  low = false;
  sort: SortKey = 'name';
  dir: SortDir = 'asc';

  user: User;
  active = true;
  showModal = new Subject<string>();
  partnerInfo$: Observable<PartnerInfo>;

  /** Cada bloco espera a SUA chamada: resumo em cima, afiliados embaixo. */
  loadingPartnerInfo = true;
  loadingAffiliates = true;
  affiliates$: Observable<AffiliatesPage>;

  /**
   * Falha da lista: `server` (500 e afins) ou `offline` (status 0, sem resposta).
   * A tela mostra "Não foi possível carregar as contas" com "Tentar de novo" —
   * nunca a lista vazia nem contas com saldo 0 (mockup aprovado, estado de erro).
   */
  affiliatesError: 'server' | 'offline' | null = null;

  /** Só a resposta da busca MAIS RECENTE mexe nas flags da lista. */
  private affiliatesRequest = 0;

  /** Linha aberta na gaveta de detalhe (`null` = gaveta fechada). */
  drawerAffiliate: Affiliate | null = null;

  get affiliateUrl(): string {
    return `${environment.signatureUrl}?src=${this.user.affiliate_code}`;
  }

  get isAgency(): boolean {
    return this.user.profile_id === AGENCY_CLIENT;
  }

  get isApiEnabled(): boolean {
    return this.isAgency && this.user.api_enabled;
  }

  get customer_success(): boolean {
    return this.user.profile_id === CUSTOMER_SUCCESS;
  }

  get administrator(): boolean {
    return this.user.profile_id === ADMINISTRATOR;
  }

  get sortSelectValue(): string {
    return `${this.sort}|${this.dir}`;
  }

  /** A chave PIX é do dono: o membro só vê (o backend devolve 403 `owner_only`). */
  get isAccountOwner(): boolean {
    return this.authService.isAccountOwner;
  }

  /**
   * "Acessar conta" simula a filha, e membro nunca simula: o backend recusa
   * com 403 `member_cannot_simulate`. O botão fica só para a agência em pessoa.
   */
  get canAccessClientAccount(): boolean {
    return this.isAgency && this.isAccountOwner;
  }

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.update();
    this.authService.userChanged.subscribe(() => this.update());
  }

  ngOnInit(): void {
    if (this.customer_success) {
      this.router.navigate(['/dashboard']);
    }
  }

  update() {
    this.user = this.authService.getUser();
    this.loadingPartnerInfo = true;
    this.partnerInfo$ = this.userService.getPartnerDashboard().pipe(
      // A flag desliga na EMISSÃO, que chega de forma assíncrona — fora do ciclo
      // de detecção que a leu, então não há leitura antes de escrita aqui.
      tap({
        next: () => { this.loadingPartnerInfo = false; },
        error: () => { this.loadingPartnerInfo = false; },
      })
    );

    this.route
      .queryParams
      .subscribe(params => {
        this.page = +params.page || this.page;
        if (params.status) {
          this.active = JSON.parse(params.status);
        }
        this.perPage = +params.perPage || this.perPage;
        this.filter = params.filter || this.filter;
        // Só os dois tipos conhecidos; sem o parâmetro (ex.: voltar no navegador
        // de `?type=wallet` para `/partner`) é "Todas" — antes o chip ficava aceso
        // e `?type=foo` ia cru para o back.
        this.type = params.type === 'wallet' || params.type === 'credits' ? params.type : 'all';
        this.low = params.low === 'true' || params.low === true;
        this.sort = params.sort === 'balance' ? 'balance' : 'name';
        this.dir = params.dir === 'desc' ? 'desc' : 'asc';

        if (this.perPage > 25) {
          this.pageChanged(this.page, 25);
          return;
        }

        this.listPaginated();
      });
  }

  listPaginated() {
    const filter: AgencyFilter = {
      text: this.filter,
      status: this.active,
      ...(this.type !== 'all' ? { type: this.type } : {}),
      ...(this.low ? { low: true } : {}),
    };

    const request = ++this.affiliatesRequest;
    const isCurrent = () => request === this.affiliatesRequest;

    this.loadingAffiliates = true;
    this.affiliatesError = null;
    this.affiliates$ = this.userService
      .findAffiliates(this.page, this.perPage, filter, this.sort, this.dir)
      .pipe(
        tap(() => {
          if (isCurrent()) {
            this.loadingAffiliates = false;
          }
        }),
        catchError(err => {
          if (isCurrent()) {
            this.affiliatesError = err?.status === 0 ? 'offline' : 'server';
          }

          return EMPTY;
        }),
        // Termina sem emitir (cancelada) também desliga: o esqueleto não pode
        // ficar girando para sempre.
        finalize(() => {
          if (isCurrent()) {
            this.loadingAffiliates = false;
          }
        }),
      );
  }

  showActives() {
    this.active = true;
    this.pageChanged(1);
  }

  showInactives() {
    this.active = false;
    this.pageChanged(1);
  }

  setType(type: TypeFilter): void {
    this.type = type;
    this.pageChanged(1);
  }

  toggleLow(): void {
    this.low = !this.low;
    this.pageChanged(1);
  }

  sortChanged(value: string): void {
    const [sort, dir] = value.split('|') as [SortKey, SortDir];
    this.sort = sort;
    this.dir = dir;
    this.pageChanged(1);
  }

  clearFilters(): void {
    this.filter = '';
    this.type = 'all';
    this.low = false;
    this.pageChanged(1);
  }

  copy(url: string): void {
    navigator.clipboard.writeText(url);
    this.toastService.success('URL copiada com sucesso!');
  }

  copyEmail(user: Affiliate): void {
    navigator.clipboard.writeText(user.email);
    this.toastService.success('E-mail copiado com sucesso!');
  }

  pageChanged(page: any, perPage: number = null): void {
    const queryParams: Record<string, any> = {
      page,
      perPage: perPage || this.perPage,
      filter: this.filter,
      status: this.active,
      type: this.type,
      low: this.low,
      sort: this.sort,
      dir: this.dir,
    };

    if (!this.filter) {
      delete queryParams.filter;
    }
    if (this.type === 'all') {
      delete queryParams.type;
    }
    if (!this.low) {
      delete queryParams.low;
    }

    this.router.navigate(['/partner'], { queryParams });
  }

  simulate(affiliate: Affiliate): void {
    if (!this.canAccessClientAccount) {
      return;
    }

    this.userService.findById(affiliate.id)
      .subscribe(user => {
        this.authService.setSimulatedUser(user);
        this.router.navigate(['/']);
      });
  }

  showPixForm() {
    this.showModal.next(this.user.pix_key);
  }

  /**
   * Exclusão de pix pede confirmação (decisão do mockup aprovado, 27/10) — a
   * recuperação de uma chave errada hoje depende de o parceiro ligar para o
   * suporte. Só muda a tela depois da confirmação do backend: numa recusa
   * (403, rede) a chave continua cadastrada e não pode sumir.
   */
  deletePixKey() {
    this.confirmationService.show(
      'Deseja realmente excluir sua chave pix?<br>Você vai precisar cadastrar uma nova para receber os próximos pagamentos.',
      () => {
        const user = { ...this.user, pix_key: null };

        this.userService.save(user).subscribe({
          next: () => {
            this.user = user;
            this.toastService.success('Chave pix excluída com sucesso!');

            if (this.authService.getSimulatedUser()) {
              this.authService.setSimulatedUser(this.user);
            } else {
              this.authService.setUser(this.user);
            }
          },
          // O aviso da recusa sai no http-error.interceptor.
          error: () => { },
        });
      }
    );
  }

  modalClosed(event: any) {
    if (event === 'YES') {
      this.update();
    }
  }

  openDrawer(affiliate: Affiliate): void {
    this.drawerAffiliate = affiliate;
  }

  closeDrawer(): void {
    this.drawerAffiliate = null;
  }
}
